import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaDownload } from "react-icons/fa";
import dayjs from "dayjs";
import styles from "../../master/academicConfiguration/Academiclist.module.css";
import reportStyles from "./Report.module.css";
import { CustomButton, CustomDropdown, CustomDatePicker } from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import {
  fetchAcademicYearOptions,
  fetchClassOptions,
  fetchSectionOptions,
  clearSectionOptions,
} from "../../../../redux/Masterdata";
import { buildReportPdfHtml, pdfBadge } from "./pdfReportTemplate";

/* All three tabs hit the same endpoint; `trans_filter` selects the slice. */
const TRANSACTION_REPORT_URL = "schoolFees/feeDashboard/transactionList";

/* Tab -> trans_filter: 0 = all, 1 = regular, 2 = admission */
const TRANS_FILTER = { all: 0, regular: 1, admission: 2 };

/* payment_mode: API expects 0 = Cash, 1 = Online ("" = All, not sent as 0/1).
 * Option values are kept as "0"/"1" strings, not real numbers — CustomDropdown
 * appears to treat a falsy `value` prop as "nothing selected" and falls back
 * to its placeholder, so a real numeric 0 (Cash) rendered identically to the
 * unselected/"All" state while 1 (Online, truthy) displayed fine. "0" is a
 * non-empty string, so it's truthy and displays correctly; Number(paymentMode)
 * in buildPayload converts it back to the 0 the API expects. */
const PAYMENT_MODE = { ONLINE: "1", CASH: "0" };

const EMPTY_SUMMARY = {
  total_transactions: 0,
  total_paid_amount: 0,
  approved_amount: 0,
  approved_percent: 0,
  pending_amount: 0,
  pending_percent: 0,
};

/* ── ₹25,000 — amounts arrive as strings ("25000.00") ── */
const formatCurrency = (val) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(val) || 0);

/* ── "2026-05-05" -> "05/05/2026"; the API already sends DD/MM/YYYY ── */
const formatDate = (val) => {
  if (!val) return "-";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(val);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return val;
};

const toApiDate = (d) => (d ? dayjs(d).format("YYYY/MM/DD") : "");

/* CustomDropdown hands back the whole option (or null when cleared). */
const optionValue = (v) => (typeof v === "object" && v !== null ? v.value : v);

/* ── Approved / Rejected / Pending Approval ── */
const ApprovalBadge = ({ status, labels }) => {
  const s = String(status || "").toLowerCase();
  let variant = reportStyles.badgePending;
  let text = labels.pendingApproval || "Pending Approval";
  if (s === "approved") {
    variant = reportStyles.badgeApproved;
    text = labels.approved || "Approved";
  } else if (s === "rejected") {
    variant = reportStyles.badgeRejected;
    text = labels.rejected || "Rejected";
  }
  return <span className={`${reportStyles.badge} ${variant}`}>{text}</span>;
};

/* ── Paid / Partly Paid ── */
const PaidStatusBadge = ({ status, labels }) => {
  const s = String(status || "").toLowerCase();
  const isPaid = s === "paid";
  return (
    <span
      className={`${reportStyles.badge} ${
        isPaid ? reportStyles.badgePaid : reportStyles.badgePartlyPaid
      }`}
    >
      {isPaid ? labels.paid || "Paid" : labels.partlyPaid || "Partly Paid"}
    </span>
  );
};

/**
 * Shared body for the three transaction tabs. Only the ID column and the
 * presence of the UPI Reference / Payment Mode columns differ between them,
 * so the tab files stay thin wrappers around this view.
 *
 * @param {"all"|"regular"|"admission"} type
 */
const TransactionsReportView = ({ type = "all" }) => {
  const dispatch = useDispatch();
  const { translations } = useSelector((state) => state.language);
  // memoised so the `|| {}` fallback doesn't create a new object each render
  // and re-trigger every useMemo/useCallback that depends on it
  const labels = useMemo(() => translations?.labels || {}, [translations]);

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const master_id = clientData?.master_id || "";

  const {
    academicYearOptions: reduxAcademicYearOptions,
    academicYearLoading,
    classOptions: reduxClassOptions,
    classLoading,
    sectionOptions: reduxSectionOptions,
    sectionLoading,
  } = useSelector((state) => state.masterData);

  // ── Filters ──
  const [academicYear, setAcademicYear] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [paymentMode, setPaymentMode] = useState("");

  // ── In-header column filters (Admin Approval / Fees Paid), server-side ──
  const [columnFilters, setColumnFilters] = useState([]);
  const [approvalStatus, setApprovalStatus] = useState("");
  const [paidStatus, setPaidStatus] = useState("");

  // ── Grid ──
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [tableLoading, setTableLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [exporting, setExporting] = useState("");

  // ── Academic years on mount ──
  useEffect(() => {
    if (school_id) dispatch(fetchAcademicYearOptions(school_id));
  }, [dispatch, school_id]);

  // ── Default-select the active year (isActive === 1) once options land ──
  useEffect(() => {
    if (academicYear || reduxAcademicYearOptions.length === 0) return;
    const active =
      reduxAcademicYearOptions.find((o) => o.isActive === 1) ||
      reduxAcademicYearOptions[0];
    if (active) setAcademicYear(active.value);
  }, [reduxAcademicYearOptions, academicYear]);

  // ── The `id` behind the selected academic_year string — the transaction
  //    payload keys off the id, the class list off the label ──
  const academicYearId = useMemo(
    () =>
      reduxAcademicYearOptions.find((o) => o.value === academicYear)?.id || "",
    [reduxAcademicYearOptions, academicYear],
  );

  // ── Classes depend on the selected academic year ──
  useEffect(() => {
    if (!school_id || !academicYear) return;
    dispatch(
      fetchClassOptions({
        id: parseInt(school_id),
        academic_year: academicYear,
      }),
    );
  }, [dispatch, school_id, academicYear]);

  // ── Dropdown options — Class/Section/Payment Mode carry an "All" entry ──
  const academicYearOptions = useMemo(
    () =>
      reduxAcademicYearOptions.map((o) => ({ value: o.value, label: o.label })),
    [reduxAcademicYearOptions],
  );

  const classOptions = useMemo(
    () => [{ value: "", label: labels.all || "All" }, ...reduxClassOptions],
    [reduxClassOptions, labels],
  );

  const sectionOptions = useMemo(
    () => [{ value: "", label: labels.all || "All" }, ...reduxSectionOptions],
    [reduxSectionOptions, labels],
  );

  const paymentModeOptions = useMemo(
    () => [
      { value: "", label: labels.all || "All" },
      { value: PAYMENT_MODE.ONLINE, label: labels.online || "Online" },
      { value: PAYMENT_MODE.CASH, label: labels.cash || "Cash" },
    ],
    [labels],
  );

  /* Values match the strings the API returns in `approval_status`. */
  const approvalStatusOptions = useMemo(
    () => [
      { value: "", label: labels.all || "All" },
      { value: "Approved", label: labels.approved || "Approved" },
      { value: "Rejected", label: labels.rejected || "Rejected" },
      {
        value: "Pending",
        label: labels.pendingApproval || "Pending Approval",
      },
    ],
    [labels],
  );

  const paidStatusOptions = useMemo(
    () => [
      { value: "", label: labels.all || "All" },
      { value: "Paid", label: labels.paid || "Paid" },
      { value: "Partly Paid", label: labels.partlyPaid || "Partly Paid" },
    ],
    [labels],
  );

  // ── Sync MRT's header selects into the payload-driving state ──
  useEffect(() => {
    const approval = columnFilters.find((f) => f.id === "approval_status");
    const paid = columnFilters.find((f) => f.id === "paid_status");
    setApprovalStatus((prev) => {
      const next = approval?.value ?? "";
      return prev === next ? prev : next;
    });
    setPaidStatus((prev) => {
      const next = paid?.value ?? "";
      return prev === next ? prev : next;
    });
    setPagination((p) => (p.pageIndex === 0 ? p : { ...p, pageIndex: 0 }));
  }, [columnFilters]);

  // ── Payload shared by the grid fetch and the two exports ──
  const buildPayload = useCallback(
    (page, limit) => ({
      school_id: Number(school_id),
      academic_year_id: Number(academicYearId),
      class_id: selectedClass ? Number(selectedClass) : "",
      section_id: selectedSection || "",
      start_date: toApiDate(fromDate),
      end_date: toApiDate(toDate),
      payment_mode: paymentMode === "" ? "" : Number(paymentMode),
      trans_filter: TRANS_FILTER[type] ?? TRANS_FILTER.all,
      search: apiSearch.trim(),
      page,
      limit,
    }),
    [
      school_id,
      academicYearId,
      type,
      selectedClass,
      selectedSection,
      paymentMode,
      fromDate,
      toDate,
      apiSearch,
    ],
  );

  const transformRows = useCallback(
    (rows, offset = 0) =>
      rows.map((item, index) => ({
        s_no: offset + index + 1,
        transaction_id: item.transaction_id || "",
        chalan_no: item.chalan_no || "",
        reference_id: item.emis_id || item.admission_id || "",
        student_name: item.student_name || "",
        class_name: item.class_name || item.class || "",
        section_id: item.section || item.section_id || "",
        mobile_number: item.mobile_number || "",
        date: item.date || "",
        time: item.time || "",
        upi_reference_id: item.upi_reference || item.upi_reference_id || "",
        payment_mode: item.payment_mode || "",
        paid_amount: Number(item.paid_amount) || 0,
        total_amount: Number(item.total_amount) || 0,
        approval_status: item.approval_status || "",
        paid_status: item.fee_status || item.paid_status || "",
      })),
    [],
  );

  /* The endpoint takes no approval/fee-status argument, so the two in-header
     selects are narrowed on the rows the server sent back. */
  const applyStatusFilters = useCallback(
    (rows) => {
      if (!approvalStatus && !paidStatus) return rows;
      const eq = (a, b) =>
        String(a || "").toLowerCase() === String(b || "").toLowerCase();
      return rows.filter(
        (r) =>
          (!approvalStatus || eq(r.approval_status, approvalStatus)) &&
          (!paidStatus || eq(r.paid_status, paidStatus)),
      );
    },
    [approvalStatus, paidStatus],
  );

  const fetchReport = useCallback(async () => {
    if (!school_id || !academicYearId) {
      setData([]);
      setRowCount(0);
      return;
    }

    setTableLoading(true);
    try {
      const response = await AxiosInstance.post(
        TRANSACTION_REPORT_URL,
        buildPayload(pagination.pageIndex + 1, pagination.pageSize),
      );

      const ok = response?.data?.status || response?.data?.success;
      const payload = response?.data?.data || {};
      if (ok && Array.isArray(payload.transactions)) {
        setData(
          transformRows(
            payload.transactions,
            pagination.pageIndex * pagination.pageSize,
          ),
        );
        setRowCount(payload.pagination?.total || payload.transactions.length);
        setSummary({
          total_transactions: Number(payload.stats?.total_transactions) || 0,
          total_paid_amount: Number(payload.stats?.total_paid_amount) || 0,
          approved_amount: Number(payload.stats?.approved_amount) || 0,
          approved_percent: Number(payload.stats?.approved_percent) || 0,
          pending_amount: Number(payload.stats?.pending_amount) || 0,
          pending_percent: Number(payload.stats?.pending_percent) || 0,
        });
      } else {
        setData([]);
        setRowCount(0);
        setSummary(EMPTY_SUMMARY);
      }
    } catch (error) {
      console.error("Error fetching transaction report:", error);
      setData([]);
      setRowCount(0);
      setSummary(EMPTY_SUMMARY);
    } finally {
      setTableLoading(false);
    }
  }, [
    school_id,
    academicYearId,
    buildPayload,
    transformRows,
    pagination.pageIndex,
    pagination.pageSize,
  ]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // ── Handlers ──
  const handleAcademicYearChange = useCallback(
    (value) => {
      const year = optionValue(value);
      if (!year) return; // required filter — never allowed to go empty
      setAcademicYear(year);
      setSelectedClass("");
      setSelectedSection("");
      dispatch(clearSectionOptions());
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    },
    [dispatch],
  );

  const handleClassChange = useCallback(
    (value) => {
      const classId = optionValue(value) || "";
      setSelectedClass(classId);
      setSelectedSection("");
      dispatch(clearSectionOptions());
      if (classId) {
        dispatch(
          fetchSectionOptions({
            id: [parseInt(classId)],
            school_id: parseInt(school_id),
            staff_id: master_id ? parseInt(master_id) : null,
            subject_staff: 1,
          }),
        );
      }
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    },
    [dispatch, school_id, master_id],
  );

  const handleSectionChange = useCallback((value) => {
    setSelectedSection(optionValue(value) || "");
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, []);

  const handlePaymentModeChange = useCallback((value) => {
    // `??`, not `||` — keeps any falsy-but-valid option value intact
    // instead of silently falling back to "" ("All").
    setPaymentMode(optionValue(value) ?? "");
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, []);

  const handleFromDateChange = useCallback((d) => {
    setFromDate(d || null);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, []);

  const handleToDateChange = useCallback((d) => {
    setToDate(d || null);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, []);

  const handleSearchEnter = useCallback((value) => {
    setApiSearch(value || "");
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, []);

  // ── Which optional columns this tab shows ──
  const showUpiReference = type !== "admission";
  const showPaymentMode = type === "all";
  const referenceHeader = useMemo(() => {
    if (type === "regular") return labels.emisId || "Emis ID";
    if (type === "admission") return labels.admissionId || "Admission ID";
    return labels.emisIdAdmissionId || "Emis ID / Admission ID";
  }, [type, labels]);

  // ── Exports pull the full filtered set, not just the visible page ──
  const fetchAllRows = useCallback(async () => {
    const response = await AxiosInstance.post(
      TRANSACTION_REPORT_URL,
      buildPayload(1, rowCount || pagination.pageSize),
    );
    const ok = response?.data?.status || response?.data?.success;
    const payload = response?.data?.data || {};
    return ok && Array.isArray(payload.transactions)
      ? applyStatusFilters(transformRows(payload.transactions))
      : [];
  }, [
    buildPayload,
    transformRows,
    applyStatusFilters,
    rowCount,
    pagination.pageSize,
  ]);

  const exportHeaders = useMemo(
    () =>
      [
        { key: "transaction_id", label: labels.transactionId || "Transaction ID" },
        { key: "chalan_no", label: labels.chalanNo || "Chalan No." },
        { key: "reference_id", label: referenceHeader },
        { key: "student_name", label: labels.studentName || "Student Name" },
        { key: "class_name", label: labels.class || "Class" },
        { key: "section_id", label: labels.section || "Section" },
        { key: "mobile_number", label: labels.mobileNumber || "Mobile Number" },
        { key: "date", label: labels.date || "Date" },
        { key: "time", label: labels.time || "Time" },
        showUpiReference && {
          key: "upi_reference_id",
          label: labels.upiReferenceId || "UPI Reference ID",
        },
        showPaymentMode && {
          key: "payment_mode",
          label: labels.paymentMode || "Payment Mode",
        },
        { key: "paid_amount", label: labels.paidAmount || "Paid Amount" },
        { key: "total_amount", label: labels.totalAmount || "Total Amount" },
        {
          key: "approval_status",
          label: labels.adminApprovalStatus || "Admin Approval Status",
          render: (v) =>
            pdfBadge(
              v || labels.pendingApproval || "Pending Approval",
              v === "Approved" ? "success" : v === "Rejected" ? "error" : "warning",
            ),
        },
        {
          key: "paid_status",
          label: labels.feesPaidStatus || "Fees Paid Status",
          render: (v) =>
            pdfBadge(
              v || labels.partlyPaid || "Partly Paid",
              v === "Paid" ? "success" : "warning",
            ),
        },
      ].filter(Boolean),
    [labels, referenceHeader, showUpiReference, showPaymentMode],
  );

  const fileBaseName = useMemo(() => {
    const tabLabel =
      type === "regular"
        ? labels.regularTransaction || "Regular Transaction"
        : type === "admission"
          ? labels.admissionTransaction || "Admission Transaction"
          : labels.all || "All";
    return `${
      labels.allFeesTransactionsReport || "All Fees Transactions Report"
    }_${tabLabel}_${academicYear || ""}`.replace(/[^\w\-.]+/g, "_");
  }, [labels, type, academicYear]);

  const handleDownloadExcel = useCallback(async () => {
    setExporting("excel");
    try {
      const rows = await fetchAllRows();
      const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const csv = [
        exportHeaders.map((h) => escape(h.label)).join(","),
        ...rows.map((r) => exportHeaders.map((h) => escape(r[h.key])).join(",")),
      ].join("\r\n");

      // BOM keeps ₹ and Tamil labels readable when Excel opens the file
      const blob = new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${fileBaseName}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting transaction report to Excel:", error);
    } finally {
      setExporting("");
    }
  }, [fetchAllRows, exportHeaders, fileBaseName]);

  const handleDownloadPdf = useCallback(async () => {
    setExporting("pdf");
    try {
      const rows = await fetchAllRows();
      const { default: html2pdf } = await import("html2pdf.js");

      const container = document.createElement("div");
      container.style.padding = "16px";
      container.innerHTML = buildReportPdfHtml({
        title:
          labels.allFeesTransactionsReport || "All Fees Transactions Report",
        infoItems: [
          clientData.school_name,
          `${labels.academicYear || "Academic Year"} ${academicYear}`,
          `${labels.reportDate || "Report Date"}: ${dayjs().format("DD/MM/YYYY")}`,
        ],
        headers: exportHeaders,
        rows,
      });

      await html2pdf()
        .set({
          margin: 8,
          filename: `${fileBaseName}.pdf`,
          html2canvas: { scale: 2 },
          jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
        })
        .from(container)
        .save();
    } catch (error) {
      console.error("Error exporting transaction report to PDF:", error);
    } finally {
      setExporting("");
    }
  }, [
    fetchAllRows,
    exportHeaders,
    fileBaseName,
    labels,
    academicYear,
    clientData.school_name,
  ]);

  // ── Success / pending share — the API sends both, fall back to the ratio ──
  const { successPercent, pendingPercent } = useMemo(() => {
    const total = Number(summary.total_paid_amount) || 0;
    const ratio = (part) => (total ? ((Number(part) || 0) / total) * 100 : 0);
    return {
      successPercent: summary.approved_percent || ratio(summary.approved_amount),
      pendingPercent: summary.pending_percent || ratio(summary.pending_amount),
    };
  }, [summary]);

  // ── Rows the grid renders, after the two header selects ──
  const visibleData = useMemo(
    () => applyStatusFilters(data),
    [applyStatusFilters, data],
  );

  // ── Columns ──
  const columns = useMemo(
    () =>
      [
        {
          accessorKey: "transaction_id",
          header: labels.transactionId || "Transaction ID",
          size: 130,
          enableColumnFilter: false,
        },
        {
          accessorKey: "chalan_no",
          header: labels.chalanNo || "Chalan No.",
          size: 120,
          enableColumnFilter: false,
        },
        {
          accessorKey: "reference_id",
          header: referenceHeader,
          size: 170,
          enableColumnFilter: false,
        },
        {
          accessorKey: "student_name",
          header: labels.studentName || "Student Name",
          size: 150,
          enableColumnFilter: false,
        },
        {
          accessorKey: "class_name",
          header: labels.class || "Class",
          size: 90,
          enableColumnFilter: false,
        },
        {
          accessorKey: "section_id",
          header: labels.section || "Section",
          size: 100,
          enableColumnFilter: false,
        },
        {
          accessorKey: "mobile_number",
          header: labels.mobileNumber || "Mobile Number",
          size: 140,
          enableColumnFilter: false,
        },
        {
          accessorKey: "date",
          header: labels.date || "Date",
          size: 120,
          enableColumnFilter: false,
          Cell: ({ cell }) => formatDate(cell.getValue()),
        },
        {
          accessorKey: "time",
          header: labels.time || "Time",
          size: 100,
          enableColumnFilter: false,
        },
        showUpiReference && {
          accessorKey: "upi_reference_id",
          header: labels.upiReferenceId || "UPI Reference ID",
          size: 160,
          enableColumnFilter: false,
          Cell: ({ cell }) => cell.getValue() || "-",
        },
        showPaymentMode && {
          accessorKey: "payment_mode",
          header: labels.paymentMode || "Payment Mode",
          size: 130,
          enableColumnFilter: false,
        },
        {
          accessorKey: "paid_amount",
          header: labels.paidAmount || "Paid Amount",
          size: 130,
          enableColumnFilter: false,
          Cell: ({ cell }) => formatCurrency(cell.getValue()),
        },
        {
          accessorKey: "total_amount",
          header: labels.totalAmount || "Total Amount",
          size: 130,
          enableColumnFilter: false,
          Cell: ({ cell }) => formatCurrency(cell.getValue()),
        },
        {
          accessorKey: "approval_status",
          header: labels.adminApprovalStatus || "Admin Approval Status",
          size: 190,
          minSize: 190,
          maxSize: 190,
          enableColumnFilter: true,
          filterVariant: "select",
          filterSelectOptions: approvalStatusOptions,
          Cell: ({ cell }) => (
            <ApprovalBadge status={cell.getValue()} labels={labels} />
          ),
        },
        {
          accessorKey: "paid_status",
          header: labels.feesPaidStatus || "Fees Paid Status",
          size: 170,
          minSize: 170,
          maxSize: 170,
          enableColumnFilter: true,
          filterVariant: "select",
          filterSelectOptions: paidStatusOptions,
          Cell: ({ cell }) => (
            <PaidStatusBadge status={cell.getValue()} labels={labels} />
          ),
        },
      ].filter(Boolean),
    [
      labels,
      referenceHeader,
      showUpiReference,
      showPaymentMode,
      approvalStatusOptions,
      paidStatusOptions,
    ],
  );

  return (
    <>
      {/* ── Filters ── */}
      <div className={`row ${reportStyles.filterRow}`}>
        <div className="col-lg-3 col-md-6 col-sm-12 px-2">
          <CustomDropdown
            label={labels.academicYear || "Academic Year"}
            name="academicYear"
            value={academicYear}
            onChange={handleAcademicYearChange}
            options={academicYearOptions}
            placeholder={labels.selectAcademicYear || "Select Academic Year"}
            isLoading={academicYearLoading}
            required
            isClearable={false}
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12 px-2">
          <CustomDropdown
            label={labels.class || "Class"}
            name="class"
            value={selectedClass}
            onChange={handleClassChange}
            options={classOptions}
            placeholder={labels.selectClass || "Select Class"}
            isLoading={classLoading}
            required
            isClearable={false}
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12 px-2">
          <CustomDropdown
            label={labels.section || "Section"}
            name="section"
            value={selectedSection}
            onChange={handleSectionChange}
            options={sectionOptions}
            placeholder={labels.selectSection || "Select Section"}
            isLoading={sectionLoading}
            disabled={!selectedClass}
            required
            isClearable={false}
          />
        </div>
        <div className="col-lg-3 col-md-12 col-sm-12 px-2">
          <div className="row">
            <div className="col-6 px-1">
              <CustomDatePicker
                label={labels.dateRange || "Date Range"}
                name="fromDate"
                value={fromDate}
                onChange={handleFromDateChange}
                placeholder={labels.fromDate || "From Date"}
                format="DD/MM/YYYY"
                size="sm"
                maxDate={toDate || undefined}
              />
            </div>
            <div className="col-6 px-1">
              <CustomDatePicker
                label="&nbsp;"
                name="toDate"
                value={toDate}
                onChange={handleToDateChange}
                placeholder={labels.toDate || "To Date"}
                format="DD/MM/YYYY"
                size="sm"
                minDate={fromDate || undefined}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12 px-2">
          <CustomDropdown
            label={labels.paymentMode || "Payment Mode"}
            name="paymentMode"
            value={paymentMode}
            onChange={handlePaymentModeChange}
            options={paymentModeOptions}
            placeholder={labels.selectPaymentMode || "Select Payment Mode"}
            isClearable={false}
          />
        </div>
      </div>

      {/* ── Summary cards ── */}
      <div className={`row ${reportStyles.summaryRow}`}>
        <div className="col-lg-3 col-md-6 px-2 mb-2">
          <div
            className={`${reportStyles.summaryCard} ${reportStyles.summaryCardStudents}`}
          >
            <div className={reportStyles.summaryCardTop}>
              <span
                className={`${reportStyles.summaryValue} ${reportStyles.summaryValueStudents}`}
              >
                {summary.total_transactions}
              </span>
              <span className={reportStyles.summaryLabel}>
                {labels.totalTransactions || "Total Transactions"}
              </span>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6 px-2 mb-2">
          <div
            className={`${reportStyles.summaryCard} ${reportStyles.summaryCardAverage}`}
          >
            <div className={reportStyles.summaryCardTop}>
              <span
                className={`${reportStyles.summaryValue} ${reportStyles.summaryValueAverage}`}
              >
                {formatCurrency(summary.total_paid_amount)}
              </span>
              <span className={reportStyles.summaryLabel}>
                {labels.totalPaidAmount || "Total Paid Amount"}
              </span>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6 px-2 mb-2">
          <div
            className={`${reportStyles.summaryCard} ${reportStyles.summaryCardAmount}`}
          >
            <div className={reportStyles.summaryCardTop}>
              <span
                className={`${reportStyles.summaryValue} ${reportStyles.summaryValueAmount}`}
              >
                {formatCurrency(summary.approved_amount)}
              </span>
              <span className={reportStyles.summaryLabel}>
                {labels.approvedTransactions || "Approved Transactions"}
              </span>
            </div>
            <div className={reportStyles.summaryFoot}>
              {successPercent.toFixed(1)}% {labels.successRate || "Success"}
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6 px-2 mb-2">
          <div
            className={`${reportStyles.summaryCard} ${reportStyles.summaryCardOutstanding}`}
          >
            <div className={reportStyles.summaryCardTop}>
              <span
                className={`${reportStyles.summaryValue} ${reportStyles.summaryValueOutstanding}`}
              >
                {formatCurrency(summary.pending_amount)}
              </span>
              <span className={reportStyles.summaryLabel}>
                {labels.pendingApproval || "Pending Approval"}
              </span>
            </div>
            <div className={reportStyles.summaryFoot}>
              {pendingPercent.toFixed(1)}% {labels.pendingRate || "Pending"}
            </div>
          </div>
        </div>
      </div>

      {/* ── Exports ── */}
      <div className={reportStyles.toolbar}>
        <CustomButton
          type="button"
          variant="contained"
          className={`${styles.downloadButton} `}
          fullWidth={false}
          onClick={handleDownloadExcel}
          disabled={exporting !== "" || tableLoading}
        >
          <FaDownload style={{ marginRight: "8px" }} />
          {exporting === "excel"
            ? labels.downloading || "Downloading..."
            : labels.downloadAsExcel || "Download as Excel"}
        </CustomButton>
        <CustomButton
          type="button"
          variant="contained"
          className={`${styles.downloadButton} `}
          fullWidth={false}
          onClick={handleDownloadPdf}
          disabled={exporting !== "" || tableLoading}
        >
          <FaDownload style={{ marginRight: "8px" }} />
          {exporting === "pdf"
            ? labels.downloading || "Downloading..."
            : labels.downloadAsPdf || "Download as PDF"}
        </CustomButton>
      </div>

      {/* ── Table ── */}
      <CustomMRTTable
        columns={columns}
        data={visibleData}
        loading={tableLoading}
        pagination={pagination}
        rowCount={rowCount}
        onPaginationChange={setPagination}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        onSearchEnter={handleSearchEnter}
        placeholder={
          labels.searchByStudentNameChalanId ||
          "Search by student name, Chalan ID"
        }
        enableSearch={true}
        enableFiltering={true}
        manualPagination={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        localization={{
          noRecordsToDisplay: labels.noRecordsFound || "No records found",
        }}
      />
    </>
  );
};

export default TransactionsReportView;
